import React from 'react';
import { useNotificationContext } from '../../../global/hooks/useNotificationContext';
import { Popup } from '@progress/kendo-react-popup';
import { Button, ButtonHandle } from '@progress/kendo-react-buttons';
import { Card, CardBody, CardFooter, CardHeader, CardTitle } from '@progress/kendo-react-layout';
import { useDocumentFormContext } from '../../../context/form/DocumentFormContext';
import { useCurrentUserContext } from '../../../context/currentUser/CurrentUserContext';
import { NotificationCenterItem } from '../../../context/notification/NotificationCenterItem';
import { NotificationItem } from './NotificationItem';
import { useGlobalizationContext } from '../../../context/globalization/GlobalizationContext';
import { useTranslation } from '../../../context/globalization/useTranslation';

interface NotificationDropdownProps {
  anchorElement: ButtonHandle | null;
  show: boolean;
  onClose: () => void;
}

export const NotificationDropdown = ({ anchorElement, show, onClose }: NotificationDropdownProps) => {
  const { centerNotifications, removeCenterNotification, clearAllCenterNotifications } = useNotificationContext();
  const { openRetryForm } = useDocumentFormContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { selectedIndex, setSelectedIndex } = useCurrentUserContext();

  const handleClickClearAll = () => {
    clearAllCenterNotifications();
    onClose();
  };

  const handleRetry = (notification: NotificationCenterItem) => {
    if (notification.formRetryData) {
      if (notification.formRetryData.retryIndex.id !== selectedIndex?.id && setSelectedIndex) {
        setSelectedIndex(notification.formRetryData.retryIndex);
      }

      openRetryForm(notification.formRetryData);
      onClose();
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        show &&
        !(event.target as Element).closest('.notificationDropdown') &&
        !(event.target as Element).closest('.notificationBellButton')
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [show, onClose]);

  return (
    <Popup
      anchor={anchorElement?.element}
      anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
      popupAlign={{ horizontal: 'right', vertical: 'top' }}
      show={show}
      popupClass="notificationDropdown"
      animate={true}
    >
      <Card className="notificationCard">
        <CardHeader>
          <CardTitle className="notificationCardTitle">Recent Actions</CardTitle>
        </CardHeader>
        <CardBody className="notificationCardBody">
          <ul className="notificationList">
            {centerNotifications.map(notification => (
              <li
                key={notification.id}
                className={`notificationItem ${notification.read ? 'notificationItemRead' : ''} ${
                  notification.type === 'progress' ? 'notificationItemProgress' : ''
                }`}
              >
                <NotificationItem
                  notification={notification}
                  onRetry={() => handleRetry(notification)}
                  onRemove={() => removeCenterNotification(notification.id)}
                />
              </li>
            ))}
          </ul>
        </CardBody>
        {centerNotifications.length > 0 && (
          <CardFooter>
            <Button onClick={handleClickClearAll}>{translate('custom.notifications.clearAll')}</Button>
          </CardFooter>
        )}
      </Card>
    </Popup>
  );
};
